// Client-side auth: app-JWT storage + the session lifecycle (refresh before expiry, inactivity
// logout). Ported from florence's App/DevApp auth loop. Google sign-in itself is in components/Login.jsx.
//
// AUTH is active only when REACT_APP_GOOGLE_CLIENT_ID is set (deploy). Empty = local dev: no login
// screen, no token — matching the server's AUTH_ENABLED=false. Set BOTH together for a gated deploy.
import { useCallback, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { refreshSession } from "./services/api";

const TOKEN_KEY = "token";
export const AUTH_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

const INACTIVITY_MS = 30 * 60 * 1000; // sign out after 30 min idle (florence DevApp parity)
const REFRESH_THRESHOLD_MS = 2 * 60 * 1000; // refresh when <2 min to expiry
const TICK_MS = 60 * 1000; // check once a minute

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function decodeUser(token) {
  try {
    const d = jwtDecode(token);
    return { email: d.email || d.sub, exp: d.exp };
  } catch {
    return null;
  }
}

// The single auth gate used by App.jsx. Returns { token, authEnabled, email, login, logout }.
export function useAuthGate() {
  const [token, setToken] = useState(getToken());
  const lastActivity = useRef(Date.now());
  const refreshing = useRef(false);

  const login = useCallback((appToken) => {
    localStorage.setItem(TOKEN_KEY, appToken);
    lastActivity.current = Date.now();
    setToken(appToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  // Track user activity (DOM events + the 'api-activity' signal from the axios request interceptor).
  useEffect(() => {
    if (!AUTH_ENABLED || !token) return undefined;
    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["click", "keydown", "scroll", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    window.addEventListener("api-activity", bump);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      window.removeEventListener("api-activity", bump);
    };
  }, [token]);

  // Refresh-before-expiry + inactivity-logout loop.
  useEffect(() => {
    if (!AUTH_ENABLED || !token) return undefined;
    const check = async () => {
      const now = Date.now();
      if (now - lastActivity.current > INACTIVITY_MS) { logout(); return; }
      const decoded = decodeUser(token);
      if (!decoded?.exp) { logout(); return; } // undecodable -> sign out
      const msToExpiry = decoded.exp * 1000 - now;
      if (msToExpiry < REFRESH_THRESHOLD_MS && !refreshing.current) {
        refreshing.current = true;
        try {
          const { access_token } = await refreshSession(token);
          if (access_token) login(access_token);
        } catch {
          logout();
        } finally {
          refreshing.current = false;
        }
      }
    };
    check();
    const id = setInterval(check, TICK_MS);
    return () => clearInterval(id);
  }, [token, login, logout]);

  return { token, authEnabled: AUTH_ENABLED, email: token ? decodeUser(token)?.email : null, login, logout };
}
